import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';

export const DEFAULT_DRIVER_NOTES_TEMPLATE = `NON-FOOD | FOOD PACKAGING ONLY
TOTAL CARTONS: [quantity]
REQUIREMENT: TAKE A PICTURE OF THE SIGNED INVOICE, IT'S A MUST.`;

export type DeliveryFormSettings = {
  driverNotesTemplate: string;
  defaultSpecialRequests: string[];
};

const SETTINGS_KEY = 'delivery_form_settings';

const DEFAULT_SETTINGS: DeliveryFormSettings = {
  driverNotesTemplate: DEFAULT_DRIVER_NOTES_TEMPLATE,
  defaultSpecialRequests: [],
};

function parseSettings(raw: string | undefined): DeliveryFormSettings {
  if (!raw?.trim()) return { ...DEFAULT_SETTINGS };
  try {
    const parsed = JSON.parse(raw) as Partial<DeliveryFormSettings>;
    return {
      driverNotesTemplate:
        typeof parsed.driverNotesTemplate === 'string' && parsed.driverNotesTemplate.trim()
          ? parsed.driverNotesTemplate
          : DEFAULT_SETTINGS.driverNotesTemplate,
      defaultSpecialRequests: Array.isArray(parsed.defaultSpecialRequests)
        ? parsed.defaultSpecialRequests.map((item) => String(item ?? '').trim()).filter(Boolean)
        : [],
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export async function fetchDeliveryFormSettings(): Promise<DeliveryFormSettings> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('dispatch_settings')
    .select('value')
    .eq('key', SETTINGS_KEY)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  return parseSettings((data as { value?: string } | null)?.value);
}

export async function saveDeliveryFormSettings(settings: DeliveryFormSettings): Promise<void> {
  const db = getDbSupabase('dem');
  const payload: DeliveryFormSettings = {
    driverNotesTemplate: settings.driverNotesTemplate.trim() || DEFAULT_DRIVER_NOTES_TEMPLATE,
    defaultSpecialRequests: settings.defaultSpecialRequests.map((item) => item.trim()).filter(Boolean),
  };
  const { error } = await db.from('dispatch_settings').upsert({
    key: SETTINGS_KEY,
    value: JSON.stringify(payload),
  });
  if (error) throw new Error(formatSupabaseError(error));
}

export function applyDriverNotesTemplate(template: string): string {
  return template.trim() || DEFAULT_DRIVER_NOTES_TEMPLATE;
}
